import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit'
import * as favoritesApi from '@/api/favorites'
import { logoutUser } from '@/features/auth/authSlice'

/**
 * Fetch the user's favorites list from the API
 */
export const fetchFavorites = createAsyncThunk(
  'favorites/fetchFavorites',
  async (_, { rejectWithValue }) => {
    try {
      const response = await favoritesApi.getFavorites()
      // Extract items from JSON:API envelope
      const items = response?.data?.attributes?.items || []
      return items
    } catch (error) {
      // Gracefully handle 404 (endpoint not deployed yet)
      if (error.response?.status === 404) {
        console.warn('[favorites] API endpoint not available yet, returning empty list')
        return []
      }
      return rejectWithValue(error.apiError || { message: error.message })
    }
  }
)

/**
 * Toggle an item as favorite/unfavorite
 * Optimistically updates the UI, then calls the API
 * @param {{type: string, id: string|number}} payload
 */
export const toggleFavorite = createAsyncThunk(
  'favorites/toggleFavorite',
  async ({ type, id, name }, { rejectWithValue }) => {
    try {
      const response = await favoritesApi.toggleFavorite(type, id)
      const items = response?.data?.attributes?.items || []
      return items
    } catch (error) {
      // Gracefully handle 404 (endpoint not deployed yet)
      if (error.response?.status === 404) {
        console.warn('[favorites] Toggle API not available yet')
        // Return null to signal optimistic update should stay
        return null
      }
      return rejectWithValue(error.apiError || { message: error.message })
    }
  }
)

/**
 * Reorder favorites by saving the full list with updated sort_order values
 * @param {Array<{type: string, id: string|number, sort_order: number}>} items
 */
export const reorderFavorites = createAsyncThunk(
  'favorites/reorderFavorites',
  async (items, { rejectWithValue }) => {
    try {
      const response = await favoritesApi.saveFavorites(items)
      const updatedItems = response?.data?.attributes?.items || []
      return updatedItems
    } catch (error) {
      if (error.response?.status === 404) {
        console.warn('[favorites] Save API not available yet')
        return items // Keep the local reorder
      }
      return rejectWithValue(error.apiError || { message: error.message })
    }
  }
)

const initialState = {
  items: [],
  loading: false,
  error: null,
}

const favoritesSlice = createSlice({
  name: 'favorites',
  initialState,
  reducers: {
    /**
     * Clear favorites (e.g. on logout or tenant switch)
     */
    clearFavorites: (state) => {
      state.items = []
      state.loading = false
      state.error = null
    },
  },
  extraReducers: (builder) => {
    // fetchFavorites
    builder.addCase(fetchFavorites.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(fetchFavorites.fulfilled, (state, action) => {
      state.items = action.payload
      state.loading = false
      state.error = null
    })
    builder.addCase(fetchFavorites.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload?.message || 'Failed to fetch favorites'
    })

    // toggleFavorite
    builder.addCase(toggleFavorite.pending, (state, action) => {
      // Optimistic update: toggle the item in/out of the list
      const { type, id, name } = action.meta.arg
      const existingIndex = state.items.findIndex(
        (item) => item.type === type && String(item.id) === String(id)
      )
      if (existingIndex >= 0) {
        // Remove it
        state.items.splice(existingIndex, 1)
      } else {
        // Add it at the end
        state.items.push({
          type,
          id: String(id),
          name: name || '',
          sort_order: state.items.length,
        })
      }
    })
    builder.addCase(toggleFavorite.fulfilled, (state, action) => {
      // If API returned updated list, use it; otherwise keep optimistic update
      if (action.payload !== null) {
        state.items = action.payload
      }
    })
    builder.addCase(toggleFavorite.rejected, (state, action) => {
      // Revert optimistic update would be complex; for now just log
      // The next fetchFavorites will correct the state
      state.error = action.payload?.message || 'Failed to toggle favorite'
    })

    // reorderFavorites
    builder.addCase(reorderFavorites.pending, (state, action) => {
      // Optimistic: apply the reorder immediately
      state.items = action.meta.arg
    })
    builder.addCase(reorderFavorites.fulfilled, (state, action) => {
      state.items = action.payload
    })
    builder.addCase(reorderFavorites.rejected, (state, action) => {
      state.error = action.payload?.message || 'Failed to reorder favorites'
    })

    // Clear favorites on logout
    builder.addCase(logoutUser.fulfilled, () => initialState)
    builder.addCase(logoutUser.rejected, () => initialState)
  },
})

export const { clearFavorites } = favoritesSlice.actions

// --- Selectors ---

const selectFavoritesItems = (state) => state.favorites?.items

/**
 * Select all favorite items sorted by sort_order.
 *
 * Memoized with createSelector: an inline `[...items].sort(...)` returns a
 * new array reference on every call, which Redux's `useSelector` warns about
 * ("returned a different result when called with the same parameters") since
 * every dispatched action — not just favorites ones — re-runs it and any
 * effect/memo keyed on its identity.
 */
export const selectFavorites = createSelector([selectFavoritesItems], (items) =>
  [...(items || [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
)

/**
 * Select whether favorites are loading
 */
export const selectFavoritesLoading = (state) => state.favorites?.loading || false

/**
 * Create a selector to check if a specific item is favorited
 * Usage: selectIsFavorite(state, 'dashboard', '123')
 */
export const selectIsFavorite = (state, type, id) => {
  const items = state.favorites?.items || []
  return items.some(
    (item) => item.type === type && String(item.id) === String(id)
  )
}

/**
 * Memoized selector factory for use in components
 * Usage: const isFav = useAppSelector(state => makeSelectIsFavorite(state, 'dashboard', id))
 */
export const makeSelectIsFavorite = createSelector(
  [(state) => state.favorites?.items || [], (_, type) => type, (_, __, id) => id],
  (items, type, id) =>
    items.some((item) => item.type === type && String(item.id) === String(id))
)

export default favoritesSlice.reducer
